import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject } from 'rxjs';

import { AppConfig } from '../../../app.settings';
import { MonitorHub } from '../../../shared-module/hubs/monitor.hub';
import { Launcher } from '../../models/entities/launcher.type';
import { ILauncherFeature } from '../../interfaces/launcher-feature.interface';

@Injectable()
export class LaunchersService {
  public localLaunchersSubject = new BehaviorSubject<Launcher[]>([]);
  public hideMawChromeSettings: Subject<void> = new Subject();

  private endpoint: string;
  private utilsEndpoint: string;
  private adminEndpoint: string;

  constructor(
    private readonly http: HttpClient,
    private readonly config: AppConfig,
    private readonly monHub: MonitorHub
  ) {
    this.monHub.LocalDataSubject.subscribe((data) => {
      this.localLaunchersSubject.next(data);
    });

    this.endpoint = `${this.config.getConfig('api')}Utils`;
    this.utilsEndpoint = `${this.config.getConfig('api')}Utils/Launchers`;
    this.adminEndpoint = `${this.config.getConfig('api')}Admin/Launchers`;
  }

  getAllGeneric(): Observable<Launcher[]> {
    return this.http.get<Launcher[]>(`${this.utilsEndpoint}/GetAllGeneric`);
  }

  getAllLocalByMachine(mac: string): Observable<Launcher[]> {
    return this.http.get<Launcher[]>(
      `${this.utilsEndpoint}/GetLocalByMachine/${mac}`
    );
  }

  getAllLocal(): Observable<Launcher[]> {
    return this.http.get<Launcher[]>(`${this.utilsEndpoint}/GetAllLocal`);
  }

  getAllLaunchers(): Observable<Launcher[]> {
    return this.http.get<Launcher[]>(`${this.utilsEndpoint}/GetAll`);
  }

  getVideoDuration(url: string): Observable<number> {
    const params: HttpParams = new HttpParams()
      .set('videoUrl', url);

    return this.http.get<number>(`${this.endpoint}/GetYoutubeVideoDuration`, { params });
  }

  addLauncher(model): Observable<Launcher> {
    return this.http.post<Launcher>(`${this.adminEndpoint}/AddLauncher`, model);
  }

  updateLauncher(model): Observable<Launcher> {
    return this.http.post<Launcher>(
      `${this.adminEndpoint}/UpdateLauncher`,
      model
    );
  }

  updateLaunchersPositions(launchersPositions: Partial<Launcher>[]): Observable<void> {
    return this.http.put<void>(`${this.adminEndpoint}/UpdateLaunchersPositions`, launchersPositions);
  }

  uploadLauncherImage(image: FormData): Observable<string> {
    return this.http.post<string>(`${this.adminEndpoint}/UploadLauncherImage`, image);
  }

  setDefaultLauncherImage(id: number): Observable<string> {
    return this.http.post<string>(`${this.adminEndpoint}/SetDefaultLauncherImage/${id}`, null);
  }

  setLauncher(model: Launcher) {
    return this.http.post<Launcher>(`${this.adminEndpoint}/SetLauncher`, model);
  }

  deleteLauncher(id: number) {
    return this.http.delete(`${this.adminEndpoint}/DeleteLauncher/${id}`);
  }

  getLauncherFeatures(launcherId: number, playlistItemId: number): Observable<ILauncherFeature[]> {
    return this.http.get<ILauncherFeature[]>(`${this.utilsEndpoint}/get-launcher-features/${launcherId}/${playlistItemId}`);
  }

  setFeaturesToLauncher(launcherFeature: ILauncherFeature): Observable<void> {
    return this.http.post<void>(`${this.utilsEndpoint}/set-features-to-launcher`, launcherFeature);
  }
}
